/* istanbul ignore file */
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator } from '@ngrx/store';
import { of } from 'rxjs';
import { exhaustMap, take } from 'rxjs/operators';
import { LoadingSpinnerService } from '@arc/unified-payments-frontend/shared-payment';

/**
 * Factory that creates a reusable non-dispatching NgRx functional effect that
 * shows a loading spinner from the moment the sign-in banner is submitted until
 * the sign-in email request completes (success or error).
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiLoadUntilSignInEmailSentEffect =
 *     createLoadUntilSignInEmailSentEffect({
 *       triggerAction: PoiMakePaymentComponentActions.signInBannerSignedIn,
 *       completeActions: [
 *         MakePaymentPaymentLinksApiActions.signInEmailSent,
 *         MakePaymentPaymentLinksApiActions.signInEmailError,
 *       ],
 *     });
 */
export function createLoadUntilSignInEmailSentEffect<
  A extends Action<string>,
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  completeActions: ActionCreator<string, Creator>[];
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      loadingSpinnerService = inject(LoadingSpinnerService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        exhaustMap(() => {
          const completeActions$ = actions$.pipe(
            ofType(...config.completeActions),
            take(1),
          );
          loadingSpinnerService.loadUntil(completeActions$);
          return of();
        }),
      ),
    { functional: true, dispatch: false },
  );
}

/**
 * Factory that creates a reusable non-dispatching NgRx functional effect that
 * shows a loading spinner from the moment email verification is received until
 * the platform JWT request completes (success or error).
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiLoadUntilIssuePlatformJwtEffect =
 *     createLoadUntilIssuePlatformJwtEffect({
 *       triggerAction: MakePaymentPaymentLinksApiActions.emailVerificationReceived,
 *       completeActions: [
 *         MakePaymentPaymentLinksApiActions.platformJwtObtained,
 *         MakePaymentPaymentLinksApiActions.platformJwtError,
 *       ],
 *     });
 */
export function createLoadUntilIssuePlatformJwtEffect<
  A extends Action<string>,
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  completeActions: ActionCreator<string, Creator>[];
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      loadingSpinnerService = inject(LoadingSpinnerService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        exhaustMap(() => {
          const completeActions$ = actions$.pipe(
            ofType(...config.completeActions),
            take(1),
          );
          loadingSpinnerService.loadUntil(completeActions$);
          return of();
        }),
      ),
    { functional: true, dispatch: false },
  );
}
